import * as Sentry from "@sentry/react";
import { CHAT_TIMEOUT_MS, CHAT_OUTPUT_TOKENS, PROVIDER_MODELS } from '../../constants';

export function getProviderModel(provider) {
  return PROVIDER_MODELS[provider]?.[0] || 'gemini-2.5-flash';
}

function getOpenAIText(data) {
  if (data.choices && data.choices[0] && data.choices[0].message) {
    return data.choices[0].message.content.trim();
  }
  if (data.output_text) return data.output_text;

  return data.output
    ?.flatMap((item) => item.content || [])
    .map((content) => content.text || '')
    .join('')
    .trim();
}

function getGeminiText(data) {
  return data.candidates?.[0]?.content?.parts
    ?.map((part) => part.text || '')
    .join('')
    .trim();
}

async function fetchJsonWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = new Error(data.error?.message || `API request failed with status ${response.status}`);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  } catch (error) {
    if (error.name !== 'AbortError') {
      Sentry.captureException(error, {
        extra: { url, method: options.method }
      });
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

function getOpenAIFinishReason(data) {
  return data.choices?.[0]?.finish_reason || data.incomplete_details?.reason || data.status || null;
}

function getGeminiFinishReason(data) {
  return data.candidates?.[0]?.finishReason || null;
}

export function isNormalFinishReason(reason) {
  return !reason || ['completed', 'end_turn', 'stop_sequence', 'STOP', 'stop'].includes(reason);
}

export function isTokenLimitFinishReason(reason) {
  if (typeof reason !== 'string') return false;
  const normalized = reason.toLowerCase();
  return normalized === 'length' || normalized === 'max_tokens' || normalized === 'max_output_tokens';
}

import { auth } from '../../firebase';
import { IS_MOCK_DEMO } from '../../constants';

export async function generateText({ provider, apiKey, model, prompt, instructions, maxOutputTokens = CHAT_OUTPUT_TOKENS, timeoutMs = CHAT_TIMEOUT_MS, action_type = 'chat_reply', metadata = {}, userRole = null, userData = null }) {
  const modelToUse = model || getProviderModel(provider);

  try {
    // BYOK Subscription Gate (#426)
    const sinkRole = userData?.productRoles?.sink || '';
    if (apiKey && sinkRole === 'byok' && userRole !== 'owner') {
      const createdAt = userData?.createdAt;
      const userCreatedAtMs = createdAt ? (typeof createdAt.toMillis === 'function' ? createdAt.toMillis() : new Date(createdAt).getTime()) : null;

      const FREE_TRIAL_DAYS = 14;
      const nowMs = Date.now();

      let isEntitled = false;
      const plan = userData?.plan;
      if (['light', 'standard', 'deep'].includes(plan)) {
        isEntitled = true;
      } else {
        // For 'free', 'byok', or undefined plan, check 2-week free window
        if (userCreatedAtMs == null || nowMs < userCreatedAtMs + FREE_TRIAL_DAYS * 24 * 60 * 60 * 1000) {
          isEntitled = true;
        }
      }

      if (!isEntitled) {
        const err = new Error('プランの有効期限が切れています。BYOKの利用にはLightプラン以上のサブスクリプションが必要です。');
        err.status = 402;
        err.code = 'entitlement_required';
        throw err;
      }
    }

    // owner / credit_beta: always use proxy.
    // standard_user: use proxy only when no BYOK key is configured (BYOK takes priority).
    // byok_user / unauthenticated: BYOK direct only — never route to proxy.
    const useProxy = !IS_MOCK_DEMO && (
      userRole === 'owner' ||
      userRole === 'credit_beta' ||
      (userRole === 'standard_user' && !apiKey)
    );
    if (useProxy) {
      const user = auth.currentUser;
      if (!user) {
         throw new Error('ログインしていません。');
      }
      const idToken = await user.getIdToken();
      const data = await fetchJsonWithTimeout('/api/v1/generate', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          taskType: action_type,
          action_type,
          provider,
          model: modelToUse,
          instructions,
          prompt,
          max_tokens: maxOutputTokens,
          metadata,
        }),
      }, timeoutMs);

      return {
        text: data.text || '応答を取得できませんでした。',
        finishReason: data.finish_reason || null,
      };
    }

    if (!apiKey) {
      throw new Error('APIキーが設定されていません。Settings からAPIキーを登録してください。');
    }

    if (provider === 'gemini') {
      const data = await fetchJsonWithTimeout(`https://generativelanguage.googleapis.com/v1beta/models/${modelToUse}:generateContent`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-goog-api-key': apiKey,
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                { text: `${instructions}\n\n${prompt}` },
              ],
            },
          ],
          generationConfig: {
            maxOutputTokens,
          },
        }),
      }, timeoutMs);

      return {
        text: getGeminiText(data) || '応答を取得できませんでした。',
        finishReason: getGeminiFinishReason(data),
      };
    }

    if (provider === 'anthropic') {
      const data = await fetchJsonWithTimeout('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true'
        },
        body: JSON.stringify({
          model: modelToUse,
          max_tokens: maxOutputTokens,
          system: instructions,
          messages: [{ role: 'user', content: prompt }]
        }),
      }, timeoutMs);

      return {
        text: data.content?.filter((c) => c.type === 'text').map((c) => c.text).join('').trim() || '応答を取得できませんでした。',
        finishReason: data.stop_reason || null,
      };
    }

    let endpoint = 'https://api.openai.com/v1/chat/completions';
    let body = {
      model: modelToUse,
      messages: [
        { role: 'system', content: instructions },
        { role: 'user', content: prompt }
      ],
      max_tokens: maxOutputTokens,
    };

    if (modelToUse === 'gpt-5') {
      endpoint = 'https://api.openai.com/v1/responses';
      body = {
        model: modelToUse,
        input: prompt,
        instructions: instructions,
        max_output_tokens: maxOutputTokens,
      };
    }

    const data = await fetchJsonWithTimeout(endpoint, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(body),
    }, timeoutMs);

    if (modelToUse === 'gpt-5') {
      const responseText = data.output_text ? data.output_text.trim() : (data.output?.flatMap((item) => item.content || []).map((content) => content.text || '').join('').trim() || '');
      const finishReason = data.incomplete_details?.reason || data.status || null;
      return {
        text: responseText || '応答を取得できませんでした。',
        finishReason: finishReason,
      };
    }

    return {
      text: getOpenAIText(data) || '応答を取得できませんでした。',
      finishReason: getOpenAIFinishReason(data),
    };
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        provider,
        model: modelToUse,
        action_type
      }
    });
    throw error;
  }
}
